import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { addAddOn, AddOn, removeAddOn } from '../features/addOns/addOnsSlice';
import styles from '../sass/components/RegularServiceList.module.scss';
import { GET_ADD_ON_PRICES } from '../util/gql';
import { sortServices } from '../util/sortServices';
import Button from './Button';
import Loader from './Loader';
import ProductCard from './ProductCard';
import { ServiceType } from '../interfaces/WorkOrder';
import {
  selectBestSlotAdjustmentLabel,
  selectBestSlotAdjustmentPercent,
} from '../features/booking/bookingSlice';
import { fromPriceDollars } from '../util/bookingPricing';
import { serviceNameText, untranslatedLang } from '../util/serviceText';

export default function AddOnList() {
  const { t } = useTranslation();
  const vehicleId = useAppSelector((state) => state.car.id);
  const userVehicleId = useAppSelector((state) => state.car.userVehicleId);
  const { id: customerId } = useAppSelector((state) => state.customer);

  const selectedServices = useAppSelector(
    (state) => state.serviceTypes.services,
  );
  const hasSubscription = selectedServices?.some(
    (s) => (s?.serviceTypeName ?? '') === ServiceType.subscription,
  );

  const {
    loading, error, data, refetch,
  } = useQuery(GET_ADD_ON_PRICES, {
    variables: { vehicleId, customerId, userVehicleId },
    fetchPolicy: 'network-only',
  });

  const dispatch = useAppDispatch();
  const selectedAddOns = useAppSelector((state) => state.addOns.addOns);
  const bestSlotAdjustmentPercent = useAppSelector(
    selectBestSlotAdjustmentPercent,
  );
  const bestSlotAdjustmentLabel = useAppSelector(
    selectBestSlotAdjustmentLabel,
  );

  const add = (addon: AddOn) => {
    const effectivePrice = hasSubscription ? 0 : addon.value;
    const effectiveDiscounted = hasSubscription ? 0 : addon.discountedValue;

    dispatch(addAddOn({
      ...addon,
      value: effectivePrice,
      discountedValue: effectiveDiscounted,
    }));
  };
  if (loading) {
    return (
      <div className={styles.empty}>
        <Loader />
        {t('common.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.empty}>
        {t('addOns.loadError')}
        <Button onClick={refetch}>{t('common.refresh')}</Button>
      </div>
    );
  }

  if (data?.getAddOnPrices?.length > 0) {
    const sortedAddOns = sortServices(data.getAddOnPrices);
    return (
      <>
        {sortedAddOns.map((item: AddOn) => {
          const effectivePrice = hasSubscription ? 0 : item.value;
          const effectiveDiscounted = hasSubscription
            ? 0 : item.discountedValue;
          const name = serviceNameText(item);
          return (
            <ProductCard
              key={item.id}
              details={item.description}
              detailsLang={untranslatedLang(false)}
              title={name.text}
              titleLang={untranslatedLang(name.isTranslated)}
              price={effectivePrice}
              isEstimate={item.isEstimate}
              selected={selectedAddOns.some((addOn) => addOn.id === item.id)}
              onSelect={() => add(item)}
              onRemove={() => dispatch(removeAddOn(item.id))}
              discountedPrice={effectiveDiscounted}
              fromPrice={hasSubscription ? null : fromPriceDollars(
                { ...item, discountedValue: effectiveDiscounted },
                bestSlotAdjustmentPercent,
              )}
              fromPriceLabel={bestSlotAdjustmentLabel}
            />
          );
        })}
        <p className={styles.taxNote}>{t('services.taxNote')}</p>
      </>
    );
  }

  return <div>{t('addOns.empty')}</div>;
}
